import ChartDataLabels from "chartjs-plugin-datalabels"
import { Pie } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js"
import { Box, Typography } from "@material-ui/core"
import { keys } from "lodash"
import { getOrCreateTooltip } from "../../utils/tooltip"
import LegendItem from "../LegendItem"
import { STATUS_COLORS } from "../../../constants"
import { Status } from "../../../types/common"
import { useTranslations } from "./useTranslations"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"

ChartJS.register(ArcElement, Tooltip, ChartDataLabels)

const useUpcomingProjectsStatusBasedPieChart = ({ projectsDashboard }: UpcomingProjectsStatusBasedPieChartProps) => {
  const translations = useTranslations()
  const { totalUpcomingProjects, groupedNotStartedProjects, groupedInProgressProjects } = projectsDashboard

  const notStartedStatusAndSubStatus = keys(groupedNotStartedProjects)
  const notStartedColorVariations = STATUS_COLORS.NOT_STARTED
  const notStartedLegendData = notStartedStatusAndSubStatus.map((status, index) => {
    const { numberOfProjects: value, percentageOfProjects, projects } = groupedNotStartedProjects[status]
    const label = status === Status.NOT_STARTED ? translations.notStarted : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = notStartedColorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfProjects?.toFixed(1)
    return { label, boxColor, value, percentage, projects }
  })

  const inProgressStatusAndSubStatus = keys(groupedInProgressProjects)
  const inProgressCOlorVariations = STATUS_COLORS.IN_PROGRESS
  const inProgressLegendData = inProgressStatusAndSubStatus.map((status, index) => {
    const { numberOfProjects: value, percentageOfProjects, projects } = groupedInProgressProjects[status]
    const label = status === Status.IN_PROGRESS ? translations.inProgress : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = inProgressCOlorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfProjects?.toFixed(1)
    return { label, boxColor, value, percentage, projects }
  })

  const combinedLegendData = [...inProgressLegendData, ...notStartedLegendData]
  const combinedData = {
    labels: combinedLegendData.map((item) => item.label),
    datasets: [
      {
        label: "Projects",
        data: combinedLegendData.map((item) => item.percentage),
        backgroundColor: combinedLegendData.map((item) => item.boxColor),
        borderColor: combinedLegendData.map((item) => item.boxColor),
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        external: function (context: any) {
          const { chart, tooltip } = context
          let tooltipEl = getOrCreateTooltip(chart)

          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          const dataIndex = tooltip.dataPoints[0]?.dataIndex
          if (dataIndex !== undefined) {
            const { label, percentage, value, projects } = combinedLegendData[dataIndex]
            let innerHtml = `<div style="font-weight: bold; margin-bottom: 4px;">${label}: ${percentage}% (${value})</div>`
            innerHtml += "<div>"
            projects.forEach((task: string) => {
              innerHtml += `<div>• ${task}</div>`
            })
            innerHtml += "</div>"

            tooltipEl.innerHTML = innerHtml
          }

          // Position the tooltip
          const position = context.chart.canvas.getBoundingClientRect()
          tooltipEl.style.opacity = "1"
          tooltipEl.style.left = position.left + window.pageXOffset + tooltip.caretX + "px"
          tooltipEl.style.top = position.top + window.pageYOffset + tooltip.caretY + "px"
        },
      },
      datalabels: {
        formatter: (percentage: number) => {
          const value = Math.round((percentage / 100) * totalUpcomingProjects)
          return percentage > 5 ? `${percentage}%\n${value}` : ""
        },
        color: "#fff",
        font: {
          size: 14,
          weight: "bold" as const,
        },
        anchor: "center" as const,
        align: "end" as const,
      },
    },
  }

  const displayPieChart = totalUpcomingProjects > 0
  return { data: combinedData, options, displayPieChart, legendData: combinedLegendData, translations }
}

const UpcomingProjectsStatusBasedPieChart = ({ projectsDashboard }: UpcomingProjectsStatusBasedPieChartProps) => {
  const { data, options, displayPieChart, legendData, translations } = useUpcomingProjectsStatusBasedPieChart({
    projectsDashboard,
  })

  if (!displayPieChart) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }
  return (
    <Box display={"flex"} flexDirection="row" justifyContent="space-around" flexWrap={"wrap"}>
      <Box
        style={{
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <Pie data={data} options={options} />
      </Box>
      <Box>
        {legendData.map((item, index) => (
          <LegendItem
            key={index}
            label={item.label}
            color={item.boxColor}
            percentage={item.percentage}
            count={item.value}
          />
        ))}
      </Box>
    </Box>
  )
}

export default UpcomingProjectsStatusBasedPieChart
type UpcomingProjectsStatusBasedPieChartProps = {
  projectsDashboard: ProjectsDashboardViewModel
}
